import React, { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { EnterFullScreenIcon } from '@radix-ui/react-icons';
import ChevronLeftIcon from '@/components/ui/icons/ChevronLeftIcon';
import ChevronRightIcon from '@/components/ui/icons/ChevronRightIcon';
import { monthDayTime, prettyDate } from '@/utils/dates';
import { Frame, ScoutLocation } from '@/types/location';
import CopyToClipboard from '@/components/ui/shared/CopyToClipboard';
import { useUser } from '@/hooks/useUser';
import { useScout } from '@/hooks/useScout';
import Quarantine from '@/components/ui/common/Quarantine';
import { Views } from '@/types/view';
import AddToDemo from '../../common/AddToDemo';
import Compass from './Compass';

interface Props {
  locationId: string;
  addedToDemo: string[];
  setAddedToDemo: Dispatch<SetStateAction<string[]>>;
  sortedSequences: Frame[][];
  activeSequence: Frame[];
  activeSequenceIndex: number;
  activeLocation: ScoutLocation | undefined;
  activeView: Views;
  setActiveSequenceIndex: Dispatch<SetStateAction<number>>;
  activeFrameIndex: { value: number };
  setActiveFrameIndex: Dispatch<SetStateAction<{ value: number }>>;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

const Carousel: React.FC<Props> = ({
  locationId,
  addedToDemo,
  setAddedToDemo,
  sortedSequences,
  activeSequence,
  activeSequenceIndex,
  activeView,
  activeLocation,
  setActiveSequenceIndex,
  activeFrameIndex,
  setActiveFrameIndex,
  setShowModal,
}) => {
  const { user } = useUser();
  const { quarantined, setQuarantined } = useScout();

  const bearing = activeSequence[activeFrameIndex.value]?.bearing;

  const isLastFrame =
    activeFrameIndex.value === activeSequence.length - 1 &&
    activeSequenceIndex === sortedSequences.length - 1;

  const isFirstFrame =
    activeFrameIndex.value === 0 && activeSequenceIndex === 0;

  const handleNavigation = useCallback(
    (direction: 'left' | 'right') => {
      if (direction === 'right') {
        if (isLastFrame) {
          setActiveSequenceIndex(0);
          setActiveFrameIndex({ value: 0 });
          return;
        }

        if (activeFrameIndex.value === activeSequence.length - 1) {
          setActiveSequenceIndex(prevState => {
            return prevState === sortedSequences.length - 1
              ? prevState
              : prevState + 1;
          });
          setActiveFrameIndex({ value: 0 });
        } else {
          setActiveFrameIndex({ value: activeFrameIndex.value + 1 });
        }
      } else if (direction === 'left') {
        if (isFirstFrame) return;

        if (activeFrameIndex.value === 0) {
          setActiveSequenceIndex(prevState => {
            return prevState > 0 ? prevState - 1 : prevState;
          });

          setActiveFrameIndex({
            value: sortedSequences[activeSequenceIndex - 1]
              ? sortedSequences[activeSequenceIndex - 1].length - 1
              : 0,
          });
        } else {
          setActiveFrameIndex({ value: activeFrameIndex.value - 1 });
        }
      }
    },
    [
      activeFrameIndex.value,
      activeSequence.length,
      isFirstFrame,
      isLastFrame,
      setActiveFrameIndex,
      activeSequenceIndex,
      setActiveSequenceIndex,
      sortedSequences,
    ],
  );

  const eventKeyCallback = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'f':
          // Only show the fullscreen carousel if it's on location detail view.
          if (activeView === Views.Location && activeLocation) {
            setShowModal(prev => !prev);
          }
          break;
        case 'ArrowLeft':
          handleNavigation('left');
          break;
        case 'ArrowRight':
          handleNavigation('right');
          break;
        default:
          break;
      }
    },
    [activeLocation, activeView, handleNavigation, setShowModal],
  );

  useEffect(() => {
    document.addEventListener('keydown', eventKeyCallback, false);

    return () => {
      document.removeEventListener('keydown', eventKeyCallback, false);
    };
  }, [eventKeyCallback]);

  return (
    <>
      <div
        data-quarantined={quarantined.includes(
          activeSequence[activeFrameIndex.value]?.sequence,
        )}
        className="relative flex w-full data-[quarantined=true]:opacity-50">
        <div className="absolute left-2 top-2 z-10 flex cursor-pointer items-center text-xs">
          <div className="mr-2">
            {activeSequence[activeFrameIndex.value]?.position.lat.toFixed(5)},{' '}
            {activeSequence[activeFrameIndex.value]?.position.lon.toFixed(5)}
          </div>
          <CopyToClipboard
            content={`${activeSequence[activeFrameIndex.value]?.position.lat}, ${activeSequence[activeFrameIndex.value]?.position.lon}`}
          />
        </div>
        {!!bearing && (
          <div className="absolute right-10 top-2 z-10 p-0.5">
            <Compass bearing={bearing} />
          </div>
        )}
        <div
          className="absolute right-2 top-2 z-10 cursor-pointer"
          onClick={() => setShowModal(true)}>
          <EnterFullScreenIcon className="size-6 fill-neutral-0" />
        </div>
        <div className="absolute bottom-2 left-3 z-10 flex flex-col gap-2 text-xs">
          {user?.admin && (
            <>
              <AddToDemo
                addedToDemo={addedToDemo}
                setAddedToDemo={setAddedToDemo}
                locationId={locationId}
                activeSequence={activeSequence}
                activeFrameIndex={activeFrameIndex.value}
              />
              <Quarantine
                quarantined={quarantined}
                setQuarantined={setQuarantined}
                activeSequence={activeSequence}
                activeFrameIndex={activeFrameIndex.value}
              />
            </>
          )}
          <div>
            {prettyDate(
              activeSequence[activeFrameIndex.value]?.timestamp,
              true,
            )}
            {', '}
            {monthDayTime(
              new Date(activeSequence[activeFrameIndex.value]?.timestamp),
            )}
          </div>
        </div>
        <div className="absolute bottom-2 right-3 z-10 text-xs font-semibold">
          {activeFrameIndex.value + 1} / {activeSequence.length}
        </div>
        <div
          data-first={isFirstFrame}
          className="absolute left-2 top-1/2 z-10 translate-y-1/2 cursor-pointer opacity-100 data-[first=true]:cursor-auto data-[first=true]:opacity-25"
          onClick={() => handleNavigation('left')}>
          <ChevronLeftIcon />
        </div>
        <div
          className="absolute right-2 top-1/2 z-10 translate-y-1/2 cursor-pointer opacity-100 data-[first=true]:cursor-auto data-[first=true]:opacity-25"
          onClick={() => handleNavigation('right')}>
          <ChevronRightIcon />
        </div>
        <div className="w-full overflow-hidden rounded-md">
          {activeSequence.map((frame: Frame, index: number) => {
            return (
              <div
                data-active={activeFrameIndex.value === index}
                className="aspect-h-1 aspect-w-2 flex data-[active=false]:hidden"
                key={frame.url}>
                <img
                  className="size-full select-none object-cover"
                  src={frame.url}
                  alt={`Active frame}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Carousel;
